import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  XCircle,
  ExternalLink,
  ChevronRight,
  Bot,
  CheckCircle2,
  AlertCircle,
  Save,
  Menu,
  MoreHorizontal,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadingState } from '@reflex/ui/components/shared/LoadingState';
import { SectionLabel, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { JsonSyntax } from '@reflex/ui/components/content/log-viewer';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { FlowGraph } from './FlowGraph';
import { FlowStatusBadge } from './FlowStatusBadge';
import { FlowNodeTypeIcon, FlowNodeStatusIcon } from './FlowNodeIcons';
import { FlowRunStreamView } from './FlowRunStreamView';
import { FlowRunChatView } from './FlowRunChatView';
import { FlowRunAgentSheet } from './FlowRunAgentSheet';
import { SaveAsFlowReplayDialog } from '../SaveAsFlowReplayDialog';
import {
  useFlow,
  useFlowRun,
  useFlowRunNodes,
  useFlowVersion,
  useSubmitApproval,
  useCancelFlowRun,
  useTriggerFlow,
} from '../useFlows';
import { useFlowRunStream } from '../useFlowRunStream';
import { isRunTerminal } from '@reflex/flows';
import type { FlowDefinition, FlowRunNode } from '@reflex/flows';
import { cn } from '@reflex/ui/lib/utils';
import { formatDurationRange } from '@reflex/ui/lib/format';
import { useActiveOrgSlug, useAuth } from '@reflex/ui/client/context';
import { ROUTES, withOrgSlug } from '@reflex/shared/routes';
import { ApiError } from '@reflex/ui/client/http';

type PageView = 'timeline' | 'graph' | 'stream';

/**
 * Graph fields of a version snapshot (`VersionDetail.data`). The versioning
 * service erases the payload to `unknown`; the server normalizes snapshots to
 * the current graph schema on read, so a structural check (mirroring the
 * server's own publish-path guard) is enough — a strict node parse could
 * reject an old snapshot whose node config schema has since tightened.
 */
function versionGraph(data: unknown): Pick<FlowDefinition, 'nodes' | 'edges'> | null {
  const graph = data as { nodes?: unknown; edges?: unknown } | null | undefined;
  if (!Array.isArray(graph?.nodes) || !Array.isArray(graph?.edges)) return null;
  return {
    nodes: graph.nodes as FlowDefinition['nodes'],
    edges: graph.edges as FlowDefinition['edges'],
  };
}

function NodeDetailPanel({
  node,
  runId,
  onOpenAgent,
}: {
  node: FlowRunNode;
  runId: string;
  onOpenAgent?: (agentId: string) => void;
}) {
  const slug = useActiveOrgSlug();
  const output = node.output as Record<string, unknown> | null | undefined;
  const input = node.input as Record<string, unknown> | null | undefined;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {<FlowNodeTypeIcon type={node.nodeType} />}
          <PanelTitle as="h3" size="sm">
            {node.nodeName}
          </PanelTitle>
        </div>
        <FlowStatusBadge status={node.status} />
      </div>

      {node.iteration > 1 && (
        <div className="text-xs text-muted-foreground">Iteration {node.iteration}</div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {node.startedAt && <span>Started {new Date(node.startedAt).toLocaleTimeString()}</span>}
        {node.completedAt && (
          <span>Completed {new Date(node.completedAt).toLocaleTimeString()}</span>
        )}
        {node.startedAt && (
          <span>Duration: {formatDurationRange(node.startedAt, node.completedAt)}</span>
        )}
      </div>

      {node.agentId && (
        <div className="flex flex-col gap-1.5">
          {onOpenAgent && (
            <button
              type="button"
              onClick={() => onOpenAgent(node.agentId!)}
              className="inline-flex items-center gap-1.5 rounded-md border border-status-info/20 bg-status-info/5 px-3 py-2 text-xs font-medium text-status-info transition-colors hover:bg-status-info/10"
            >
              <Bot className="h-3.5 w-3.5" />
              View Agent
            </button>
          )}
          <Link
            to={withOrgSlug(slug, `/agents/${node.agentId}`)}
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Open agent full page
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      {node.outputLabel && (
        <div>
          <SectionLabel as="span" className="text-[11px]">
            Output Label
          </SectionLabel>
          <p className="mt-0.5 rounded bg-muted/50 px-2 py-1 font-mono text-xs">
            {node.outputLabel}
          </p>
        </div>
      )}

      {node.approvalResponse && (
        <div>
          <SectionLabel as="span" className="text-[11px]">
            Approval Response
          </SectionLabel>
          <div className="mt-1 rounded border bg-muted/30 p-2 text-xs">
            <div className="flex items-center gap-1.5">
              {(node.approvalResponse.approved ?? false) ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-status-success" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5 text-status-error" />
              )}
              <span className="font-medium">
                {node.approvalResponse.decision
                  ? node.approvalResponse.decision.replace(/_/g, ' ')
                  : (node.approvalResponse.approved ?? false)
                    ? 'Approved'
                    : 'Rejected'}
              </span>
            </div>
            {node.approvalResponse.feedback && (
              <p className="mt-1.5 whitespace-pre-wrap text-muted-foreground">
                {node.approvalResponse.feedback}
              </p>
            )}
          </div>
        </div>
      )}

      {input && Object.keys(input).length > 0 && (
        <div>
          <SectionLabel as="span" className="text-[11px]">
            Input
          </SectionLabel>
          <pre className="mt-1 max-h-40 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">
            <JsonSyntax value={input} />
          </pre>
        </div>
      )}

      {output && Object.keys(output).length > 0 && (
        <div>
          <SectionLabel as="span" className="text-[11px]">
            Output
          </SectionLabel>
          <pre className="mt-1 max-h-60 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">
            <JsonSyntax value={output} />
          </pre>
        </div>
      )}

      {!node.startedAt && node.status === 'pending' && (
        <p className="text-xs italic text-muted-foreground">
          Waiting for upstream nodes to complete.
        </p>
      )}

      {/* Link to flow run node details for debugging */}
      {node.agentId && (
        <p className="text-[10px] text-muted-foreground">
          Agent ID: {node.agentId} · Run node: {runId}:{node.nodeId}:{node.iteration}
        </p>
      )}
    </div>
  );
}

export interface FlowRunDetailProps {
  runId: string | undefined;
  /** Inbox-only mobile sidebar toggle. When provided, a hamburger button is rendered before the back link. */
  onToggleSidebar?: () => void;
  /** Invoked when the API returns 404 for this run id. Callers can navigate away (e.g. cross-org redirect). */
  onNotFound?: () => void;
}

export function FlowRunDetail({ runId, onToggleSidebar, onNotFound }: FlowRunDetailProps) {
  const slug = useActiveOrgSlug();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data: run, isLoading, error: runError } = useFlowRun(runId);
  const { data: flow } = useFlow(run?.flowId);
  // Runs pinned to a published version render that immutable snapshot, not
  // the current draft: what actually ran must stay answerable after the flow
  // is edited. Unpinned runs (drafts, built-ins) have no snapshot to fetch.
  const { data: executedVersion, isError: executedVersionError } = useFlowVersion(
    run?.flowId,
    run?.flowVersionId,
  );
  const { data: runNodes } = useFlowRunNodes(runId);
  const submitApproval = useSubmitApproval();
  const cancelRun = useCancelFlowRun();
  const triggerFlow = useTriggerFlow();
  // Read the selected node from `?nodeId=` so deep-links from the agent
  // chat (`agent.source.flow` / `agent.flow_result` bubbles) can scroll
  // and expand a specific run-node on mount. The agent drilldown sheet
  // is keyed off `?agentId=` so that following a flow-run link from
  // inside the sheet clears the param and closes the panel.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedNodeId = searchParams.get('nodeId');
  const drilldownAgentId = searchParams.get('agentId');
  const setSelectedNodeId = (next: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set('nodeId', next);
        else params.delete('nodeId');
        return params;
      },
      { replace: true },
    );
  };
  const setDrilldownAgentId = (next: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set('agentId', next);
        else params.delete('agentId');
        return params;
      },
      { replace: true },
    );
  };
  const [pageView, setPageView] = useState<PageView>('timeline');
  const [saveReplayOpen, setSaveReplayOpen] = useState(false);
  const { events: streamEvents, isLoading: streamLoading } = useFlowRunStream(runId);

  // Cross-org / stale-id handling: when the server returns 404, defer to the
  // caller to navigate away. FlowRunPage uses this to drop the runId from the
  // URL; the inbox lets the surrounding selection logic resolve.
  useEffect(() => {
    if (runId && runError instanceof ApiError && runError.status === 404) {
      onNotFound?.();
    }
  }, [runId, runError, onNotFound]);

  // Scroll the sidebar entry for the deep-linked `?nodeId=` into view
  // once the run nodes have loaded so users following an
  // `agent.source.flow` / `agent.flow_result` link land on the right
  // node instead of the top of the list.
  useEffect(() => {
    if (!selectedNodeId || !runNodes) return;
    const el = document.querySelector<HTMLElement>(
      `[data-node-id="${CSS.escape(selectedNodeId)}"]`,
    );
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedNodeId, runNodes]);

  if (isLoading || !run) {
    return <LoadingState message={isLoading ? undefined : 'Run not found'} />;
  }

  // Graph the run executed: the pinned snapshot when one exists, else the
  // live flow. A pinned run whose snapshot is unavailable (fetch failed or
  // malformed data) falls back to the live flow — an approximate graph beats
  // a dead page — and stays undefined only while the snapshot loads, so the
  // draft never flashes before the executed version renders.
  const snapshotGraph = versionGraph(executedVersion?.data);
  const executedFlow: FlowDefinition | undefined = !run.flowVersionId
    ? flow
    : snapshotGraph
      ? flow && { ...flow, nodes: snapshotGraph.nodes, edges: snapshotGraph.edges }
      : executedVersionError || executedVersion
        ? flow
        : undefined;

  const waitingNodes = runNodes?.filter((n) => n.status === 'waiting_approval') ?? [];
  const isActive = !isRunTerminal(run.status);
  const isReplayable = isRunTerminal(run.status);
  // Deliberately narrower than the terminal set: retrying a `denied` run
  // would re-attempt work its consent gate refused.
  const canRetry = can('flows:run') && (run.status === 'error' || run.status === 'cancelled');

  const handleRetry = () => {
    triggerFlow.mutate(
      {
        flowId: run.flowId,
        triggerData: run.triggerData ?? undefined,
        triggerNodeId: run.triggerNodeId ?? undefined,
      },
      {
        onSuccess: (newRun) => {
          toast.success('Automation re-triggered');
          navigate(ROUTES.org(slug).flowRun(newRun.id));
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to retry automation run');
        },
      },
    );
  };

  // Hide skipped nodes (not-taken branches) from the sidebar and counter so
  // the list reflects only nodes that can still execute or have already run.
  // Sort to match the timeline/chat view: oldest startedAt first, with
  // not-yet-started nodes pinned to the bottom.
  const visibleRunNodes =
    runNodes
      ?.filter((n) => n.status !== 'skipped')
      .slice()
      .sort((a, b) => {
        const aTs = a.startedAt ?? Number.POSITIVE_INFINITY;
        const bTs = b.startedAt ?? Number.POSITIVE_INFINITY;
        return aTs - bTs;
      }) ?? [];

  const completedCount = visibleRunNodes.filter((n) => n.status === 'completed').length;
  const totalCount = visibleRunNodes.length;

  // Camera follows the running node; if nothing is running, fall back to the
  // node currently waiting for approval, then the most-recently-started node.
  const focalNodeId =
    runNodes?.find((n) => n.status === 'running')?.nodeId ??
    runNodes?.find((n) => n.status === 'waiting_approval')?.nodeId ??
    [...(runNodes ?? [])]
      .filter((n): n is FlowRunNode & { startedAt: number } => typeof n.startedAt === 'number')
      .sort((a, b) => b.startedAt - a.startedAt)[0]?.nodeId ??
    null;

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-2">
        <div className="flex items-center gap-3">
          {onToggleSidebar && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 md:hidden"
              onClick={onToggleSidebar}
              aria-label="Open sidebar"
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          <Link
            to={withOrgSlug(slug, `/automations/${run.flowId}`)}
            className="inline-flex items-center gap-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Back to automation"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="h-5 w-px bg-border" />
          <div>
            <PanelTitle size="sm">{run.flowName}</PanelTitle>
            <span className="text-[11px] text-muted-foreground">
              Started {new Date(run.createdAt).toLocaleString()}
              {run.completedAt &&
                ` · Duration ${formatDurationRange(run.createdAt, run.completedAt)}`}
              {executedVersion && ` · v${executedVersion.versionNumber}`}
              <FlowStatusBadge
                status={run.status}
                className="ml-1 border-0 px-1 py-0 text-[11px] md:hidden"
              />
            </span>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-between gap-2 md:flex-none md:gap-3">
          <div className="flex rounded-full bg-muted/80 p-0.5">
            {(['timeline', 'graph', 'stream'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setPageView(v)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors',
                  pageView === v
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <FlowStatusBadge status={run.status} className="hidden md:inline-flex" />
          {canRetry && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              disabled={triggerFlow.isPending}
              onClick={handleRetry}
              data-testid="flow-run-retry"
            >
              <RotateCcw className={cn('h-3.5 w-3.5', triggerFlow.isPending && 'animate-spin')} />
              Retry
            </Button>
          )}
          {(isActive || isReplayable) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Run actions"
                  className="h-7 w-7 p-0"
                  data-testid="flow-run-actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {isReplayable && (
                  <DropdownMenuItem
                    onSelect={() => setSaveReplayOpen(true)}
                    data-testid="save-as-flow-replay"
                  >
                    <Save className="mr-2 h-3.5 w-3.5" />
                    Save as replay
                  </DropdownMenuItem>
                )}
                {isActive && (
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={cancelRun.isPending}
                    onSelect={() => cancelRun.mutate(run.id)}
                  >
                    <XCircle className="mr-2 h-3.5 w-3.5" />
                    Cancel run
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Main layout: graph/stream + sidebar */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 md:flex-row">
        {/* Left: primary content */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          {pageView === 'timeline' && (
            <div className="min-h-0 flex-1 overflow-hidden rounded-lg border bg-card">
              <FlowRunChatView
                run={run}
                flow={executedFlow}
                runNodes={runNodes}
                onOpenAgent={setDrilldownAgentId}
                onSubmitApproval={(nodeId, response) =>
                  submitApproval.mutate({ runId: run.id, nodeId, response })
                }
                isApprovalSubmitting={submitApproval.isPending}
              />
            </div>
          )}
          {pageView === 'graph' && (
            <div className="relative min-h-0 flex-1 rounded-lg border bg-card">
              {executedFlow ? (
                <FlowGraph
                  nodes={executedFlow.nodes}
                  edges={executedFlow.edges}
                  runNodes={runNodes}
                  triggerData={run.triggerData as Record<string, unknown> | null}
                  triggerNodeId={run.triggerNodeId ?? null}
                  className="h-full w-full"
                  interactive={false}
                  autoLayout
                  layoutDirection="LR"
                  focalNodeId={focalNodeId}
                  onNodeSelect={(node) => setSelectedNodeId(node?.id ?? null)}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Loading graph...
                </div>
              )}
              {waitingNodes.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPageView('timeline')}
                  className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-status-warning/40 bg-status-warning/10 px-3 py-1 text-[11px] font-medium text-status-warning shadow-sm transition-colors hover:bg-status-warning/20"
                >
                  <AlertCircle className="h-3 w-3" />
                  {waitingNodes.length === 1
                    ? '1 pending approval'
                    : `${waitingNodes.length} pending approvals`}
                  <span className="text-muted-foreground">· Review in Timeline</span>
                </button>
              )}
            </div>
          )}
          {pageView === 'stream' && (
            <div className="min-h-0 flex-1 overflow-hidden rounded-lg border bg-card">
              <FlowRunStreamView
                events={streamEvents ?? []}
                isLoading={streamLoading}
                onOpenAgent={setDrilldownAgentId}
              />
            </div>
          )}
        </div>

        {/* Right sidebar: node list + detail */}
        <div className="flex shrink-0 flex-col overflow-hidden rounded-lg border bg-card md:w-72">
          {/* Node list */}
          <div className="shrink-0 border-b px-3 py-2">
            <SectionLabel className="text-[11px]">
              Nodes ({completedCount}/{totalCount})
            </SectionLabel>
          </div>
          <div className="flex-1 overflow-y-auto">
            {visibleRunNodes.map((node) => {
              const isSelected = selectedNodeId === node.nodeId;
              return (
                <div
                  key={node.id}
                  data-node-id={node.nodeId}
                  className="border-b border-border/50 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => setSelectedNodeId(isSelected ? null : node.nodeId)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-accent/50',
                      isSelected && 'bg-accent',
                    )}
                  >
                    {<FlowNodeStatusIcon status={node.status} />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium">{node.nodeName}</div>
                      {node.startedAt && (
                        <div className="text-[10px] text-muted-foreground">
                          {node.completedAt
                            ? formatDurationRange(node.startedAt, node.completedAt)
                            : node.status === 'running'
                              ? formatDurationRange(node.startedAt)
                              : ''}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 shrink-0 text-muted-foreground transition-transform',
                        isSelected && 'rotate-90',
                      )}
                    />
                  </button>

                  {isSelected && (
                    <div className="border-t bg-card/40">
                      <NodeDetailPanel
                        node={node}
                        runId={run.id}
                        onOpenAgent={setDrilldownAgentId}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <FlowRunAgentSheet agentId={drilldownAgentId} onClose={() => setDrilldownAgentId(null)} />
      <SaveAsFlowReplayDialog
        flowRunId={run.id}
        defaultName={`${run.flowName} replay`}
        open={saveReplayOpen}
        onOpenChange={setSaveReplayOpen}
      />
    </div>
  );
}
